import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { serviceProviderService } from 'features/service-providers/services';
import { Loading, QueryParams } from 'shared/types';
import { ServiceProvider } from 'features/service-providers/models';

interface AddingWasteTreatmentState {
  items: ServiceProvider[];
  loading: Loading;
  total: number;
  page: number;
}

const name = 'addingWasteTreatment';

const initialState: AddingWasteTreatmentState = {
  items: [],
  loading: 'idle',
  total: 0,
  page: 1,
};

export const fetchWasteTreatmentPups = createAsyncThunk(
  `${name}/fetch-waste-treatment-pups`,
  async (
    payload: { search?: string; page?: number; limit?: number; serviceProviderId?: number } | undefined,
    { rejectWithValue },
  ) => {
    try {
      const { search, page = 1, limit = 20, serviceProviderId } = payload || {};
      const response = await serviceProviderService.getWasteTreatmentPups({
        ...(search ? { searchValue: `${search}` } : {}),
        orderBy: 'name,asc',
        page,
        limit,
        ...(serviceProviderId != null ? { id: serviceProviderId } : {}),
      } as QueryParams);
      return { response, page, limit };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const addingWasteTreatmentSlice = createSlice({
  name,
  initialState,
  reducers: {
    reset(state) {
      state.items = [];
      state.loading = 'idle';
      state.total = 0;
      state.page = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWasteTreatmentPups.pending, (state) => {
        state.loading = 'pending';
      })
      .addCase(fetchWasteTreatmentPups.fulfilled, (state, action) => {
        state.loading = 'idle';
        const { response, page } = action.payload as {
          response: { rows: ServiceProvider[]; count: number };
          page: number;
          limit: number;
        };
        const rows = response?.rows || [];
        state.items = page === 1 ? rows : [...state.items, ...rows];
        state.total = response?.count ?? 0;
        state.page = page;
      })
      .addCase(fetchWasteTreatmentPups.rejected, (state) => {
        state.loading = 'idle';
      });
  },
});

export const { reset } = addingWasteTreatmentSlice.actions;

export const selectAddingWasteTreatmentItems = (state: RootState) => state.addingWasteTreatment.items;
export const selectAddingWasteTreatmentLoading = (state: RootState) => state.addingWasteTreatment.loading;

export default addingWasteTreatmentSlice.reducer;
